import { ConfigProvider, Spin, notification } from 'antd';
import { useState } from 'react';

import { Modal, TableWrapper } from '../../../../../components/index';
import { useMutationWithErrorControl } from '../../../../../hooks/useMutationWithErrorControl';
import { useCreateDockingChargeMutation } from '../../../../../services/dockingApi';
import {
  DockingBillingStatusType,
  StopoverGroupbyDockingStatisticsType,
} from '../../../../../types';
import { TablePageProps } from '../../Reports';
import { ReportsTable } from '../../styles';
import { getChargeErrorDescription } from './chargeError';
import { columns } from './columns';

const { confirm } = Modal;

const CHARGE_NOTIFICATION_KEY = 'create-docking-charge';

type DockingsTableProps = {
  onOpenDrawer: (docking: StopoverGroupbyDockingStatisticsType) => void;
  onSelectItem: (e: boolean, record: number) => void;
  dockingsIdToEdit: number[];
  checkedAll: boolean;
  onCheckedAll: (value: boolean) => void;
  selectedBillingStatus: DockingBillingStatusType[];
  onChangeBillingStatus: (status: DockingBillingStatusType[]) => void;
  onOpenObservations: (docking: StopoverGroupbyDockingStatisticsType) => void;
};

export function DockingsTable(props: TablePageProps & DockingsTableProps) {
  const {
    dataSource,
    scrollOptions,
    total,
    itemsPerPage,
    onChangePagination,
    isLoading,
    showSizeChanger,
    rowKey,
    queryPage,
    onOpenDrawer,
    onSelectItem,
    dockingsIdToEdit,
    checkedAll,
    onCheckedAll,
    selectedBillingStatus,
    onChangeBillingStatus,
    onOpenObservations,
  } = props;

  function customizeRenderEmpty() {
    return <>Não há dados cadastrados.</>;
  }

  const [createCharge] = useCreateDockingChargeMutation();
  const [chargingDockingId, setChargingDockingId] = useState<number | null>(
    null
  );

  const createChargeWithErrorControl =
    useMutationWithErrorControl(createCharge);

  function onCreateCharge(docking_id: number) {
    confirm({
      title: `Tem certeza que deseja gerar o faturamento para essa atracação?`,
      okText: 'Sim',
      cancelText: 'Não',
      async onOk() {
        setChargingDockingId(docking_id);
        notification.info({
          key: CHARGE_NOTIFICATION_KEY,
          message: 'Gerando faturamento',
          description: 'Aguarde enquanto o faturamento da atracação é gerado.',
          duration: 0,
        });

        try {
          await createChargeWithErrorControl(docking_id, {
            suppressErrorAlert: true,
          }).unwrap();
          notification.success({
            key: CHARGE_NOTIFICATION_KEY,
            message: 'Faturamento gerado',
            description: 'O faturamento da atracação foi gerado com sucesso.',
            duration: 5,
          });
        } catch (error) {
          notification.error({
            key: CHARGE_NOTIFICATION_KEY,
            message: 'Não foi possível gerar o faturamento',
            description: getChargeErrorDescription(error),
            duration: 10,
          });
        } finally {
          setChargingDockingId(null);
        }
      },
    });
  }

  return (
    <Spin spinning={isLoading} tip="Carregando...">
      <TableWrapper>
        <ConfigProvider renderEmpty={customizeRenderEmpty}>
          <ReportsTable
            rowKey={rowKey || 'key'}
            pagination={{
              position: ['bottomCenter'],
              defaultPageSize: itemsPerPage || 60,
              pageSize: 60,
              total,
              onChange: onChangePagination,
              showSizeChanger,
              current: queryPage,
            }}
            columns={columns(
              dockingsIdToEdit,
              onSelectItem,
              onOpenDrawer,
              checkedAll,
              onCheckedAll,
              onCreateCharge,
              chargingDockingId,
              selectedBillingStatus,
              onChangeBillingStatus,
              onOpenObservations
            )}
            dataSource={dataSource}
            scroll={
              scrollOptions || { y: '60vh', scrollToFirstRowOnChange: false }
            }
          />
        </ConfigProvider>
      </TableWrapper>
    </Spin>
  );
}
